import { useState } from 'react';

/* ── Button ──────────────────────────────────────────────────── */
export function Button({
  children, variant = 'primary', size = 'md',
  loading = false, disabled = false,
  icon, className = '', ...props
}) {
  const base = `btn btn-${variant} btn-${size} ${className}`;
  return (
    <button className={base} disabled={disabled || loading} {...props}>
      {loading ? <Spinner size="sm" color="current" /> : icon}
      {children}
    </button>
  );
}

/* ── Badge ───────────────────────────────────────────────────── */
export function Badge({ children, color = '#0ea5e9', bg = '#e0f2fe', style = {} }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 99,
      fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
      color, background: bg, ...style
    }}>
      {children}
    </span>
  );
}

/* ── Card ────────────────────────────────────────────────────── */
export function Card({ children, className = '', style = {}, ...props }) {
  return (
    <div className={`card ${className}`} style={style} {...props}>
      {children}
    </div>
  );
}

/* ── Spinner ─────────────────────────────────────────────────── */
export function Spinner({ size = 'md', color = 'var(--sky-500)' }) {
  const s = size === 'sm' ? 16 : size === 'lg' ? 40 : 24;
  return (
    <span style={{
      display: 'inline-block', width: s, height: s,
      border: `${size === 'sm' ? 2 : 3}px solid transparent`,
      borderTopColor: color === 'current' ? 'currentColor' : color,
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
      flexShrink: 0,
    }} />
  );
}

/* ── Empty State ─────────────────────────────────────────────── */
export function EmptyState({ icon = '📭', title, description }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{icon}</div>
      <h3 style={{ fontSize: 18, color: 'var(--text-primary)', marginBottom: 8 }}>{title}</h3>
      {description && <p style={{ fontSize: 14 }}>{description}</p>}
    </div>
  );
}

/* ── Modal ───────────────────────────────────────────────────── */
export function Modal({ isOpen, onClose, title, children, width = 520 }) {
  if (!isOpen) return null;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, animation: 'fadeIn 0.2s ease',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="scale-in"
        style={{
          background: 'var(--bg-primary)', borderRadius: 'var(--radius-lg)',
          width: '100%', maxWidth: width,
          boxShadow: 'var(--shadow-lg)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid var(--border)',
        }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18 }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', fontSize: 20, lineHeight: 1,
              width: 32, height: 32, borderRadius: 'var(--radius-sm)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'var(--transition)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--slate-100)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >✕</button>
        </div>
        <div style={{ padding: '24px' }}>{children}</div>
      </div>
    </div>
  );
}

/* ── Form Field ──────────────────────────────────────────────── */
export function FormField({ label, error, required, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <label style={{
          display: 'block', marginBottom: 6,
          fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)',
          letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>
          {label}{required && <span style={{ color: 'var(--rose-500)', marginLeft: 3 }}>*</span>}
        </label>
      )}
      {children}
      {error && (
        <p style={{ marginTop: 5, fontSize: 12, color: 'var(--rose-500)' }}>
          ⚠ {error.message || error}
        </p>
      )}
    </div>
  );
}

/* ── Input ───────────────────────────────────────────────────── */
export const inputStyle = {
  width: '100%', padding: '10px 14px',
  border: '1.5px solid var(--border)',
  borderRadius: 'var(--radius)',
  fontSize: 14, fontFamily: 'var(--font-body)',
  color: 'var(--text-primary)', background: 'var(--bg-primary)',
  transition: 'var(--transition)', outline: 'none',
};

/* ── Search Input ────────────────────────────────────────────── */
export function SearchInput({ value, onChange, placeholder = 'Search...' }) {
  return (
    <div style={{ position: 'relative' }}>
      <span style={{
        position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
        color: 'var(--text-muted)', fontSize: 15, pointerEvents: 'none',
      }}>🔍</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ ...inputStyle, paddingLeft: 36 }}
        onFocus={e => e.target.style.borderColor = 'var(--border-focus)'}
        onBlur={e => e.target.style.borderColor = 'var(--border)'}
      />
    </div>
  );
}

/* ── Table ───────────────────────────────────────────────────── */
export function Table({ columns, data, loading, emptyState }) {
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spinner size="lg" />
      </div>
    );
  }
  if (!data?.length) {
    return emptyState || <EmptyState title="No data found" />;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key || col.label} style={{
                padding: '12px 16px', textAlign: col.align || 'left',
                borderBottom: '2px solid var(--border)',
                fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: 'var(--text-secondary)',
                background: 'var(--bg-secondary)', whiteSpace: 'nowrap',
              }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={row.id || i}
              style={{ transition: 'var(--transition)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--sky-50)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              {columns.map((col) => (
                <td key={col.key || col.label} style={{
                  padding: '13px 16px',
                  borderBottom: '1px solid var(--border)',
                  color: col.muted ? 'var(--text-secondary)' : 'var(--text-primary)',
                  textAlign: col.align || 'left',
                  whiteSpace: col.nowrap ? 'nowrap' : undefined,
                }}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Pagination ──────────────────────────────────────────────── */
export function Pagination({ count, page, pageSize = 20, onChange }) {
  const total = Math.ceil(count / pageSize);
  if (total <= 1) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 16px', borderTop: '1px solid var(--border)',
      fontSize: 13, color: 'var(--text-secondary)',
    }}>
      <span>
        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, count)} of {count}
      </span>
      <div style={{ display: 'flex', gap: 6 }}>
        {Array.from({ length: Math.min(total, 7) }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            onClick={() => onChange(p)}
            style={{
              width: 32, height: 32, borderRadius: 'var(--radius-sm)',
              border: '1.5px solid',
              borderColor: p === page ? 'var(--sky-500)' : 'var(--border)',
              background: p === page ? 'var(--sky-500)' : 'transparent',
              color: p === page ? '#fff' : 'var(--text-secondary)',
              fontSize: 13, fontWeight: p === page ? 700 : 400,
              cursor: 'pointer', transition: 'var(--transition)',
            }}
          >{p}</button>
        ))}
      </div>
    </div>
  );
}
